import { useState, useEffect, useMemo } from 'react';
import { Container } from '../ui/Container/Container';
import { useScrollReveal } from './useScrollReveal';
import { DemoFlow, dEdge, MobileDemoFlow } from './LandingFlowDemo';
import type { MobileNodeItem } from './LandingFlowDemo';
import type { Node, Edge } from '@xyflow/react';

/* ── Step 2: model list with cycling selection ─────────────── */
const MODELS = [
  { name: 'Nano Banana Pro', cost: '45 cr', dot: '#9678b4' },
  { name: 'FLUX Kontext Pro', cost: '13 cr', dot: '#5b8ab5' },
  { name: 'Seedream 4.5', cost: '13 cr', dot: '#a8b44a' },
  { name: 'Recraft V3', cost: '13 cr', dot: '#e06050' },
  { name: 'Ideogram V3', cost: '20 cr', dot: '#6090d0' },
  { name: 'GPT Image 1.5', cost: '3 cr', dot: '#50b080' },
];

/* ═══════════════════════════════════════════════════════════════
   Step 1 — "Drop your inputs"
   React Flow handles positioning & edge routing automatically.
   ═══════════════════════════════════════════════════════════════ */
const S1_NODES: Node[] = [
  {
    id: 'prompt',
    type: 'mini',
    position: { x: 0, y: 10 },
    data: {
      nodeType: 'prompt',
      body: 'Create a lifestyle social media post with polished photography...',
      handleRight: true,
      width: 120,
    },
  },
  {
    id: 'media1',
    type: 'mini',
    position: { x: 10, y: 100 },
    data: {
      nodeType: 'media',
      imageSrc: '/images/lp/step1-pinterest.webp',
      imageRatio: '3/2',
      filename: 'pinterest-mood.jpg',
      handleRight: true,
      width: 105,
    },
  },
  {
    id: 'media2',
    type: 'mini',
    position: { x: 0, y: 200 },
    data: {
      nodeType: 'media',
      imageSrc: '/images/lp/step1-brand.webp',
      imageRatio: '3/2',
      filename: 'brand-shoot-v2.png',
      handleRight: true,
      width: 105,
    },
  },
  {
    id: 'generate',
    type: 'mini',
    position: { x: 240, y: 60 },
    data: {
      nodeType: 'generate',
      badge: 'Inline',
      handleLeft: true,
      imageSrc: '/images/lp/step1-generated.webp',
      footerLines: ['Nano Banana Pro · 1:1', 'Regenerate', '○ Add Variation'],
      width: 120,
    },
  },
];

const S1_EDGES: Edge[] = [
  dEdge('e1', 'prompt', 'generate', '#7ea8c9'),
  dEdge('e2', 'media1', 'generate', '#9678b4'),
  dEdge('e3', 'media2', 'generate', '#9678b4'),
];

/* ═══════════════════════════════════════════════════════════════
   Step 3 — "Generate & iterate"
   ═══════════════════════════════════════════════════════════════ */
const S3_NODES: Node[] = [
  {
    id: 'generate',
    type: 'mini',
    position: { x: 0, y: 20 },
    data: {
      nodeType: 'generate',
      badge: 'Inline',
      handleRight: true,
      imageSrc: '/images/lp/step3-generate.webp',
      footerLines: ['Nano Banana Pro · 1:1', 'Regenerate', '○ Add Variation'],
      width: 140,
    },
  },
  {
    id: 'variation',
    type: 'mini',
    position: { x: 230, y: 80 },
    data: {
      nodeType: 'variation',
      body: 'zoom and crop',
      handleLeft: true,
      imageSrc: '/images/lp/step3-variation.webp',
      footerLines: ['Regenerate'],
      width: 130,
    },
  },
];

const S3_EDGES: Edge[] = [dEdge('e1', 'generate', 'variation', '#7b6ba0')];

/* ── Mobile-only static node lists ──────────────────────────── */
const S1_MOBILE: MobileNodeItem[] = [
  { nodeType: 'prompt', body: 'Create a lifestyle social media post with polished photography...' },
  { nodeType: 'media', imageSrc: '/images/lp/step1-pinterest.webp', imageRatio: '3/2', filename: 'pinterest-mood.jpg', edgeColor: '#7ea8c9' },
  { nodeType: 'media', imageSrc: '/images/lp/step1-brand.webp', imageRatio: '3/2', filename: 'brand-shoot-v2.png', edgeColor: '#9678b4' },
  { nodeType: 'generate', badge: 'Inline', imageSrc: '/images/lp/step1-generated.webp', footerLines: ['Nano Banana Pro · 1:1', 'Regenerate'], edgeColor: '#9678b4' },
];

const S3_MOBILE: MobileNodeItem[] = [
  { nodeType: 'generate', badge: 'Inline', imageSrc: '/images/lp/step3-generate.webp', footerLines: ['Nano Banana Pro · 1:1', 'Regenerate'] },
  { nodeType: 'variation', body: 'zoom and crop', imageSrc: '/images/lp/step3-variation.webp', footerLines: ['Regenerate'], edgeColor: '#7b6ba0' },
];

export function LandingSteps() {
  const ref = useScrollReveal<HTMLDivElement>();
  const [activeModel, setActiveModel] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveModel((prev) => (prev + 1) % MODELS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-12 md:py-20 bg-white" id="how-it-works">
      <Container size="lg" className="max-w-[1100px]">
        <div ref={ref} className="lp-reveal text-center">
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-primary-500 mb-3">
            How it works
          </p>
          <h2
            className="text-[clamp(1.75rem,4vw,2.6rem)] font-semibold leading-[1.15] tracking-tight mb-3"
            style={{ color: '#1d2939' }}
          >
            Three steps. Full control.
          </h2>
          <p className="text-[clamp(0.95rem,2vw,1.1rem)] text-[#5a6478] max-w-[540px] mx-auto leading-relaxed">
            Drop nodes, connect them, generate. Everything stays visual and connected.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-5 mt-10">
          {/* ── Step 1: Drop your inputs ──────────────────── */}
          <div className="text-center max-w-[360px] mx-auto md:max-w-none">
            <p className="font-mono text-[0.6rem] text-primary-500 mb-2.5 tracking-[0.08em]">
              01 -- Drop your inputs
            </p>
            {/* Desktop: React Flow canvas */}
            <DemoFlow
              nodes={S1_NODES}
              edges={S1_EDGES}
              className="hidden md:block rounded-[14px] border border-black/[0.06] mb-3.5"
              style={{ height: 280 }}
              fitPadding={0.15}
            />
            {/* Mobile: static card stack */}
            <MobileDemoFlow items={S1_MOBILE} className="md:hidden mb-3.5" />
            <h3 className="text-[0.95rem] font-semibold text-[#1d2939] mb-1">
              Bring anything as a reference
            </h3>
            <p className="text-[0.78rem] text-[#5a6478]">
              Drop prompts, Pinterest images, old work, brand assets — anything becomes a building block on the canvas.
            </p>
          </div>

          {/* ── Step 2: Pick any model ───────────────────── */}
          <div className="text-center max-w-[360px] mx-auto md:max-w-none">
            <p className="font-mono text-[0.6rem] text-primary-500 mb-2.5 tracking-[0.08em]">
              02 -- Pick any model
            </p>
            <div className="border border-black/[0.06] rounded-[14px] mb-3.5 overflow-hidden p-3 flex flex-col gap-[5px] text-left" style={{ height: 280, background: '#fafbfd' }}>
              <div className="text-[0.55rem] text-[#8c95a6] uppercase tracking-[0.1em] mb-[2px]">
                Select model
              </div>
              {MODELS.map((m, i) => {
                const isActive = i === activeModel;
                return (
                  <div
                    key={m.name}
                    className="flex items-center gap-2.5 px-2.5 py-[7px] bg-white border rounded-[7px] text-[0.68rem] transition-all duration-300"
                    style={{
                      borderColor: isActive ? '#5b8ab5' : 'rgba(0,0,0,0.06)',
                      background: isActive ? 'rgba(91,138,181,0.08)' : 'white',
                      color: isActive ? '#1d2939' : '#5a6478',
                    }}
                  >
                    <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background: m.dot }} />
                    {m.name}
                    <span className="ml-auto font-mono text-[0.52rem] text-[#8c95a6]">{m.cost}</span>
                  </div>
                );
              })}
            </div>
            <h3 className="text-[0.95rem] font-semibold text-[#1d2939] mb-1">
              Switch models per node
            </h3>
            <p className="text-[0.78rem] text-[#5a6478]">
              60+ models from 8 providers. No lock-in. Try different models on the same prompt.
            </p>
          </div>

          {/* ── Step 3: Generate & iterate ───────────────── */}
          <div className="text-center max-w-[360px] mx-auto md:max-w-none">
            <p className="font-mono text-[0.6rem] text-primary-500 mb-2.5 tracking-[0.08em]">
              03 -- Generate & iterate
            </p>
            {/* Desktop: React Flow canvas */}
            <DemoFlow
              nodes={S3_NODES}
              edges={S3_EDGES}
              className="hidden md:block rounded-[14px] border border-black/[0.06] mb-3.5"
              style={{ height: 280 }}
              fitPadding={0.2}
            />
            {/* Mobile: static card stack */}
            <MobileDemoFlow items={S3_MOBILE} className="md:hidden mb-3.5" />
            <h3 className="text-[0.95rem] font-semibold text-[#1d2939] mb-1">
              Branch into variations
            </h3>
            <p className="text-[0.78rem] text-[#5a6478]">
              Love the output? Branch it. Try a different angle. Every version stays connected to its source.
            </p>
          </div>
        </div>
      </Container>
    </section>
  );
}
